<template>
    <div id="edit-sms-page">
        <ChannelForm
            ref="channelForm"
            :steps="formSteps"
            :current-step="currentStep"
            @update:currentStep="setStep"
        >
            <template #default="{ nextStep, previousStep, close }">
                <!-- Configure Step -->
                <StepConfigure
                    v-if="currentStep === 'configure'"
                    ref="stepConfigure"
                    :sender="sender"
                    :place-sender="localPlaceSender"
                    :channel="channel"
                    @saved="onConfigureSaved"
                    @cancel="close"
                />

                <!-- Test Step -->
                <StepTestPhone
                    v-if="currentStep === 'test'"
                    ref="stepTest"
                    :input-label="t('settings-connections-steps-enter_mobile_number')"
                    input-placeholder="(##) #####-####"
                    :input-id="'test-' + channel"
                    :testing="testing"
                    @test="testConnection"
                    @back="previousStep"
                />

                <!-- Finished Step -->
                <StepFinished
                    v-if="currentStep === 'finished'"
                    :channel="channel"
                    @back="previousStep"
                    @retry-test="setStep('test')"
                    @done="$emit('done')"
                />
            </template>
        </ChannelForm>
    </div>
</template>

<script>
import keviApi from '../../../keviApi';
import ChannelForm from './channel-form.vue';
import StepConfigure from './steps/step-configure.vue';
import StepTestPhone from './steps/step-test-phone.vue';
import StepFinished from './steps/step-finished.vue';

export default {
    name: 'edit-sms-page',
    components: {
        ChannelForm,
        StepConfigure,
        StepTestPhone,
        StepFinished,
    },
    props: {
        sender: {
            type: Object,
            required: true,
        },
        senders: {
            type: Array,
            default: () => [],
        },
        rawEditing: {
            type: Object,
            default: () => ({}),
        },
        channel: {
            type: String,
            default: 'sms',
        },
        testPath: {
            type: String,
            default: 'senders/sms/test',
        },
    },
    data() {
        return {
            currentStep: 'configure',
            testing: false,
            placeSenderId: null,
            localPlaceSender: {},
        };
    },
    computed: {
        formSteps() {
            return [
                { id: 'configure', label: this.t('settings-connections-steps-configure'), icon: 'ui-settings-02' },
                { id: 'test', label: this.t('settings-connections-steps-test'), icon: 'ui-loading-01' },
                { id: 'finished', label: this.t('settings-connections-steps-finish'), icon: 'ui-check-circle' },
            ];
        },
    },
    methods: {
        setStep(step) {
            if (['configure', 'test', 'finished'].includes(step)) {
                this.currentStep = step;
            }
        },
        onConfigureSaved(placeSender) {
            const merged = {
                ...(this.rawEditing || {}),
                ...(this.localPlaceSender || {}),
                ...(placeSender || {}),
            };
            // Normalize field values for StepConfigure (expects place_sender.fields[field.id])
            if (!merged.fields && merged.form_fields) merged.fields = merged.form_fields;
            if (!merged.fields && placeSender?.fields) merged.fields = placeSender.fields;
            this.localPlaceSender = merged;

            // Store the place_sender id for test step
            this.placeSenderId = merged.id;
            this.$emit('saved', merged);
            this.$root.$emit('reload-senders');
            // Move to next step
            this.currentStep = 'test';
        },
        async testConnection(phone) {
            this.testing = true;
            const placeSenderId = this.placeSenderId || this.localPlaceSender?.id || this.rawEditing?.id;
            
            if (!placeSenderId) {
                this.$store.dispatch('notification/error', this.$_('settings-connections-notifications-sender_id_not_found'));
                this.testing = false;
                return;
            }

            const options = keviApi.getOptions(this.testPath, "POST", {
                places_senders_id: placeSenderId,
                phone_number: phone,
            });
            
            this.$http(options).then((result) => {
                this.$store.dispatch('notification/success', this.$_('settings-connections-notifications-test_sent_success'));
                this.currentStep = 'finished';
            }, (error) => {
                const body = (error && error.body) || {};
                const detail = body.message || body.error || "";
                const errorMessage = this.$_(
                    `notifications-errors-${this.channel}-${body.error}`,
                    null,
                    this.$_('settings-connections-notifications-test_send_error', [detail])
                );
                this.$store.dispatch('notification/error', errorMessage);
            }).finally(() => {
                this.testing = false;
            });
        },
        close() {
            this.$root.$emit('close-drawer');
            this.$root.$emit('reload-senders');
        },
    },
    mounted() {
        // Initialize placeSenderId from rawEditing if editing existing
        if (this.rawEditing?.id) {
            this.placeSenderId = this.rawEditing.id;
        }
        this.localPlaceSender = this.rawEditing || {};
    },
    watch: {
        rawEditing: {
            handler() {
                if (this.rawEditing?.id) {
                    this.placeSenderId = this.rawEditing.id;
                }
                this.localPlaceSender = this.rawEditing || {};
            },
            deep: true,
            immediate: true,
        },
    },
};
</script>

<style scoped lang="less">
#edit-sms-page {
    .box {
        padding: 20px;
    }
}
</style>
